<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 v-t="'entities.user.name_plural'" class="mt-0"></h1>
      <el-switch
        v-model="editModeToggle"
        :active-text="t.editModeToggle.batchMode"
      />
    </div>

    <dx-data-grid
      ref="dataGrid"
      :columns="columns"
      :data-source="store"
      :remote-operations="remoteOperations"
      :show-borders="true"
      :show-row-lines="false"
      :show-column-lines="true"
      :row-alternation-enabled="true"
      @editor-preparing="onEditorPreparing"
      @init-new-row="onInitNewRow"
    >
      <dx-editing
        :allow-adding="true"
        :allow-updating="true"
        :allow-deleting="true"
        :mode="editMode"
      />

      <dx-sorting mode="multiple"/>

      <dx-filter-row :visible="true"/>
      <dx-filter-panel :visible="true"/>
      <dx-filter-builder-popup/>

      <dx-paging :page-size="15"/>
      <dx-pager
        :allowed-page-sizes="[15, 30, 50]"
        :visible="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :show-page-size-selector="true"
      />
    </dx-data-grid>
  </div>
</template>

<script>
import { DxDataGrid } from 'devextreme-vue';
import {
  DxEditing,
  DxFilterBuilderPopup,
  DxFilterPanel,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxSorting,
} from 'devextreme-vue/data-grid';
import { confirm } from 'devextreme/ui/dialog';
import { omit } from 'lodash';
import { mapState } from 'vuex';
import createBackendStore from '@/lib/create-backend-store';
import { buildColumns } from '@/lib/data-grid-columns';

export default {
  name: 'Users',
  components: {
    DxDataGrid,
    DxEditing,
    DxFilterRow,
    DxFilterPanel,
    DxFilterBuilderPopup,
    DxPager,
    DxPaging,
    DxSorting,
  },
  data() {
    return {
      store: null,
      columns: [],
      remoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      skipStatusNames: ['initial'],
      editModeToggle: false,
    };
  },
  computed: {
    ...mapState(['statuses']),
    t() {
      return {
        editModeToggle: {
          batchMode: this.$t('common.batchMode'),
        },
      };
    },
    editMode() {
      return this.editModeToggle ? 'batch' : 'form';
    },
  },
  methods: {
    buildColumns,
    configureColumns() {
      return [
        ...this.buildColumns('user', [
          {
            property: 'username',
            sortOrder: 'asc',
            sortIndex: 2,
          },
          {
            property: 'password',
            visible: false,
          },
          {
            property: 'role',
            sortOrder: 'asc',
            sortIndex: 1,
          },
          {
            property: 'manufacturerId',
            sortOrder: 'asc',
            sortIndex: 0,
          },
          ...this.statuses
            .filter(s => !this.skipStatusNames.includes(s.name))
            .map(status => ({
              property: 'statusMap[:id]',
              captionContext: 'short',
              options: { status },
              allowFiltering: false,
              allowSorting: false,
              width: 100,
            })),
        ]),
        {
          type: 'buttons',
          buttons: [
            'edit',
            'delete',
            {
              icon: 'fas fa-sign-out-alt',
              hint: this.$t('common.logOut'),
              onClick: event => this.logOutUser(event.row.data),
            },
          ],
        },
      ];
    },
    configureStore(columns) {
      const fields = columns
        .map(c => c.dataField)
        .filter(f => f && f !== 'password' && !f.startsWith('statusMap'));

      fields.push('statuses.id');

      // workaround for backend error:
      // ER_BAD_FIELD_ERROR: Unknown column 'distinctAlias.User_manufacturer_name' in 'field list'
      fields.push('manufacturer.name');

      return createBackendStore('/users', {
        fields,
        toRow: (user) => {
          const userStatusIds = user.statuses.map(status => status.id);
          const statusMap = {};

          this.statuses.forEach((status) => {
            statusMap[status.id] = userStatusIds.includes(status.id);
          });

          // eslint-disable-next-line no-param-reassign
          user.statusMap = statusMap;

          return user;
        },
        valuesFilter: values => omit(values, ['statusMap']),
        afterInsertOrUpdate: async (key, values) => {
          if (!values.statusMap) return;

          const promises = Object.entries(values.statusMap)
            .map(async ([statusIdString, value]) => {
              const statusId = parseInt(statusIdString, 10);

              if (value) {
                await this.$http.post(`/users/${key}/statuses`, { id: statusId });
              } else {
                await this.$http.delete(`/users/${key}/statuses/${statusId}`);
              }
            });

          await Promise.all(promises);
        },
      });
    },
    refresh() {
      this.dataGrid.refresh();
    },
    async logOutUser(user) {
      const confirmMessage = this.$t('views.users.logOutUser.confirmMessage', { user });
      const successMessage = this.$t('views.users.logOutUser.successMessage', { user });

      const result = await confirm(confirmMessage, '', false);

      if (!result) return;

      await this.$http.patch(`/users/${user.id}`, { invalidateToken: true });

      this.$message(successMessage, { user });
    },
    onEditorPreparing(event) {
      if (event.parentType === 'dataRow' && event.dataField === 'password') {
        // eslint-disable-next-line no-param-reassign
        event.editorOptions.mode = 'password';
      }
    },
    onInitNewRow(event) {
      const statusMap = {};

      this.statuses.forEach((status) => {
        statusMap[status.id] = false;
      });

      // eslint-disable-next-line no-param-reassign
      event.data.statusMap = statusMap;
    },
  },
  created() {
    this.columns = this.configureColumns();
    this.store = this.configureStore(this.columns);
  },
  mounted() {
    this.$emit('requestLayoutWidthMode', 'full');
  },
};
</script>

<style scoped>

</style>
